import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, Loader2, CheckCircle2, XCircle, Clock, 
  Database, Cpu, FileOutput, GitBranch, Play,
  Download, ExternalLink, BarChart3, FileText,
  Presentation, Table, LayoutDashboard, TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const AIWorkflow = () => {
  const [query, setQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [nodeStatuses, setNodeStatuses] = useState({}); // ✅ Track individual node statuses
  const [history, setHistory] = useState([]);
  const [examples, setExamples] = useState([]);
  const [showExamples, setShowExamples] = useState(true);
  
  const chatEndRef = useRef(null);
  const statusPollInterval = useRef(null);

  // Fetch examples on mount
  useEffect(() => {
    fetchExamples();
    fetchHistory();
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [workflow, executionStatus]);

  // Poll execution status
  useEffect(() => {
    if (executionId && isExecuting) {
      statusPollInterval.current = setInterval(() => {
        checkExecutionStatus(executionId);
      }, 2000);

      return () => {
        if (statusPollInterval.current) {
          clearInterval(statusPollInterval.current);
        }
      };
    }
  }, [executionId, isExecuting]);

  const fetchExamples = async () => {
    try {
      const response = await axios.get('/api/ai-workflow/examples');
      setExamples(response.data.examples || []);
    } catch (error) {
      console.error('Failed to fetch examples:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get('/api/ai-workflow/history');
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleGenerateAndExecute = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query');
      return;
    }

    setIsGenerating(true);
    setIsExecuting(false);
    setWorkflow(null);
    setExecutionResult(null);
    setShowExamples(false);

    try {
      // Step 1: Generate workflow
      toast.loading('Generating workflow...', { id: 'workflow-gen' });
      
      const genResponse = await axios.post('/api/ai-workflow/generate', { query });
      
      if (genResponse.data.success) {
        const generatedWorkflow = genResponse.data.workflow;
        setWorkflow(generatedWorkflow);
        toast.success(`Workflow generated: ${generatedWorkflow.workflow_name}`, { id: 'workflow-gen' });
        
        // Step 2: Execute workflow
        setIsGenerating(false);
        setIsExecuting(true);
        toast.loading('Executing workflow...', { id: 'workflow-exec' });
        
        const execResponse = await axios.post('/api/ai-workflow/execute', {
          workflow: generatedWorkflow,
          query
        });
        
        if (execResponse.data.success) {
          setExecutionId(execResponse.data.executionId);
          toast.success('Workflow execution started', { id: 'workflow-exec' });
        } else {
          throw new Error(execResponse.data.error);
        }
      } else {
        throw new Error(genResponse.data.error);
      }
    } catch (error) {
      console.error('Workflow error:', error);
      toast.error(error.response?.data?.error || error.message, { id: 'workflow-gen' });
      setIsGenerating(false);
      setIsExecuting(false);
    }
  };

  const checkExecutionStatus = async (execId) => {
    try {
      const response = await axios.get(`/api/ai-workflow/status/${execId}`);
      
      if (response.data.success) {
        setExecutionStatus(response.data);
        
        // ✅ Update node statuses in real-time
        if (response.data.nodeStatuses) {
          setNodeStatuses(response.data.nodeStatuses);
        }
        
        if (response.data.status === 'completed') {
          setIsExecuting(false);
          setExecutionResult(response.data.result);
          toast.success('Workflow completed!', { id: 'workflow-exec' });
          fetchHistory(); // Refresh history
          
          if (statusPollInterval.current) {
            clearInterval(statusPollInterval.current);
          }
        } else if (response.data.status === 'failed') {
          setIsExecuting(false);
          toast.error(`Workflow failed: ${response.data.error}`, { id: 'workflow-exec' });
          
          if (statusPollInterval.current) {
            clearInterval(statusPollInterval.current);
          }
        }
      }
    } catch (error) {
      console.error('Status check error:', error);
    }
  };

  const handleExampleClick = (exampleQuery) => {
    setQuery(exampleQuery);
    setShowExamples(false);
  };

  const getNodeIcon = (nodeType, nodeCategory) => {
    if (nodeCategory === 'input') return Database;
    if (nodeCategory === 'processing') return Cpu;
    if (nodeCategory === 'output') {
      if (nodeType.includes('slide')) return Presentation;
      if (nodeType.includes('csv') || nodeType.includes('excel')) return Table;
      if (nodeType.includes('dashboard')) return LayoutDashboard;
      if (nodeType.includes('chart')) return BarChart3;
      return FileOutput;
    }
    return GitBranch;
  };

  const getNodeColor = (nodeCategory) => {
    if (nodeCategory === 'input') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (nodeCategory === 'processing') return 'bg-purple-100 text-purple-700 border-purple-300';
    if (nodeCategory === 'output') return 'bg-green-100 text-green-700 border-green-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getNodeStatus = (nodeId) => {
    // ✅ Check real-time node statuses first (during execution)
    if (nodeStatuses[nodeId]) {
      const nodeStatus = nodeStatuses[nodeId];
      if (nodeStatus.status === 'executing') return 'executing';
      if (nodeStatus.status === 'completed') return 'success';
      if (nodeStatus.status === 'failed') return 'failed';
    }
    
    // Fallback to final execution result
    if (!executionResult || !executionResult.results) return 'pending';
    const result = executionResult.results[nodeId];
    if (!result) return 'pending';
    return result.success ? 'success' : 'failed';
  };

  const renderWorkflowGraph = () => {
    if (!workflow) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <GitBranch className="mr-2" size={20} />
              {workflow.workflow_name}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{workflow.description}</p>
          </div>
          <div className="flex items-center space-x-2">
            {isExecuting && (
              <div className="flex items-center text-blue-600">
                <Loader2 className="animate-spin mr-2" size={16} />
                <span className="text-sm font-medium">Executing...</span>
              </div>
            )}
            {executionResult && (
              <div className="flex items-center text-green-600">
                <CheckCircle2 className="mr-2" size={16} />
                <span className="text-sm font-medium">Completed</span>
              </div>
            )}
          </div>
        </div>

        {/* Workflow Graph Visualization */}
        <div className="relative">
          {/* Group nodes by category */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Input Nodes */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Input</h4>
              <div className="space-y-3">
                {workflow.nodes
                  .filter(node => node.node_category === 'input')
                  .map((node) => {
                    const Icon = getNodeIcon(node.node_type, node.node_category);
                    const status = getNodeStatus(node.node_id);
                    const nodeStatusData = nodeStatuses[node.node_id];
                    const subqueries = nodeStatusData?.subqueries || [];
                    
                    return (
                      <div
                        key={node.node_id}
                        className={`relative p-4 rounded-lg border-2 ${getNodeColor(node.node_category)} transition-all`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center flex-1">
                            <Icon size={20} className="mr-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {node.node_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {node.description}
                              </p>
                            </div>
                          </div>
                          <div className="ml-2">
                            {status === 'success' && <CheckCircle2 size={16} className="text-green-600" />}
                            {status === 'failed' && <XCircle size={16} className="text-red-600" />}
                            {status === 'executing' && <Loader2 size={16} className="animate-spin text-blue-600" />}
                            {status === 'pending' && isExecuting && <Clock size={16} className="text-gray-400" />}
                          </div>
                        </div>
                        
                        {/* Show subqueries in real-time */}
                        {subqueries.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                            {subqueries.map((subquery, idx) => (
                              <div key={idx} className="flex items-center text-xs">
                                {subquery.status === 'executing' && (
                                  <Loader2 size={12} className="animate-spin text-blue-500 mr-2" />
                                )}
                                {subquery.status === 'completed' && (
                                  <CheckCircle2 size={12} className="text-green-500 mr-2" />
                                )}
                                {subquery.status === 'pending' && (
                                  <Clock size={12} className="text-gray-400 mr-2" />
                                )}
                                <span className="text-gray-600 flex-1 truncate">
                                  {subquery.description || `Query ${subquery.index}/${subquery.total}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Processing Nodes */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Processing</h4>
              <div className="space-y-3">
                {workflow.nodes
                  .filter(node => node.node_category === 'processing')
                  .map((node) => {
                    const Icon = getNodeIcon(node.node_type, node.node_category);
                    const status = getNodeStatus(node.node_id);
                    const nodeStatusData = nodeStatuses[node.node_id];
                    const subqueries = nodeStatusData?.subqueries || [];
                    
                    return (
                      <div
                        key={node.node_id}
                        className={`relative p-4 rounded-lg border-2 ${getNodeColor(node.node_category)} transition-all`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center flex-1">
                            <Icon size={20} className="mr-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {node.node_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {node.description}
                              </p>
                            </div>
                          </div>
                          <div className="ml-2">
                            {status === 'success' && <CheckCircle2 size={16} className="text-green-600" />}
                            {status === 'failed' && <XCircle size={16} className="text-red-600" />}
                            {status === 'executing' && <Loader2 size={16} className="animate-spin text-blue-600" />}
                            {status === 'pending' && isExecuting && <Clock size={16} className="text-gray-400" />}
                          </div>
                        </div>
                        
                        {/* Show subqueries in real-time */}
                        {subqueries.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                            {subqueries.map((subquery, idx) => (
                              <div key={idx} className="flex items-center text-xs">
                                {subquery.status === 'executing' && (
                                  <Loader2 size={12} className="animate-spin text-blue-500 mr-2" />
                                )}
                                {subquery.status === 'completed' && (
                                  <CheckCircle2 size={12} className="text-green-500 mr-2" />
                                )}
                                {subquery.status === 'pending' && (
                                  <Clock size={12} className="text-gray-400 mr-2" />
                                )}
                                <span className="text-gray-600 flex-1 truncate">
                                  {subquery.description || `Query ${subquery.index}/${subquery.total}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Output Nodes */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Output</h4>
              <div className="space-y-3">
                {workflow.nodes
                  .filter(node => node.node_category === 'output')
                  .map((node) => {
                    const Icon = getNodeIcon(node.node_type, node.node_category);
                    const status = getNodeStatus(node.node_id);
                    const nodeStatusData = nodeStatuses[node.node_id];
                    const subqueries = nodeStatusData?.subqueries || [];
                    
                    return (
                      <div
                        key={node.node_id}
                        className={`relative p-4 rounded-lg border-2 ${getNodeColor(node.node_category)} transition-all`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center flex-1">
                            <Icon size={20} className="mr-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {node.node_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {node.description}
                              </p>
                            </div>
                          </div>
                          <div className="ml-2">
                            {status === 'success' && <CheckCircle2 size={16} className="text-green-600" />}
                            {status === 'failed' && <XCircle size={16} className="text-red-600" />}
                            {status === 'executing' && <Loader2 size={16} className="animate-spin text-blue-600" />}
                            {status === 'pending' && isExecuting && <Clock size={16} className="text-gray-400" />}
                          </div>
                        </div>
                        
                        {/* Show subqueries in real-time */}
                        {subqueries.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                            {subqueries.map((subquery, idx) => (
                              <div key={idx} className="flex items-center text-xs">
                                {subquery.status === 'executing' && (
                                  <Loader2 size={12} className="animate-spin text-blue-500 mr-2" />
                                )}
                                {subquery.status === 'completed' && (
                                  <CheckCircle2 size={12} className="text-green-500 mr-2" />
                                )}
                                {subquery.status === 'pending' && (
                                  <Clock size={12} className="text-gray-400 mr-2" />
                                )}
                                <span className="text-gray-600 flex-1 truncate">
                                  {subquery.description || `Query ${subquery.index}/${subquery.total}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Execution Stats */}
          {executionResult && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {executionResult.summary?.totalNodes || workflow.nodes.length}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Total Nodes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {executionResult.summary?.successfulNodes || 0}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Successful</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {executionResult.summary?.failedNodes || 0}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Failed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {(executionResult.totalDuration / 1000).toFixed(1)}s
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Duration</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (!executionResult || !executionResult.results) return null;

    const results = Object.entries(executionResult.results);
    if (results.length === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileOutput className="mr-2" size={20} />
          Workflow Results
        </h3>

        <div className="space-y-4">
          {results.map(([nodeId, result]) => {
            const node = workflow.nodes.find(n => n.node_id === nodeId);
            if (!node) return null;

            return (
              <div key={nodeId} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {node.node_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h4>
                    <p className="text-sm text-gray-600">{node.description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {result.success ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Success
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                        Failed
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {(result.executionTime / 1000).toFixed(2)}s
                    </span>
                  </div>
                </div>

                {/* Show executed queries if available */}
                {result.success && result.data && result.data.queries && Array.isArray(result.data.queries) && (
                  <div className="mt-3 p-3 bg-blue-50 rounded">
                    <p className="text-xs font-semibold text-blue-900 mb-2">
                      Executed Queries ({result.data.queries.length}):
                    </p>
                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                      {result.data.queries.slice(0, 5).map((query, i) => (
                        <li key={i} className="truncate">{query}</li>
                      ))}
                      {result.data.queries.length > 5 && (
                        <li className="text-blue-600 italic">
                          ... and {result.data.queries.length - 5} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Show result data based on node type */}
                {result.success && result.data && (
                  <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                    {/* Output nodes with downloadable content */}
                    {node.node_category === 'output' && (
                      <div className="space-y-3">
                        {/* Slide Deck */}
                        {result.data.presentationUrl && (
                          <a
                            href={result.data.presentationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink size={16} className="mr-2" />
                            View Presentation
                          </a>
                        )}
                        {result.data.exportUrl && (
                          <a
                            href={result.data.exportUrl}
                            download
                            className="flex items-center text-blue-600 hover:text-blue-700"
                          >
                            <Download size={16} className="mr-2" />
                            Download PPTX
                          </a>
                        )}
                        
                        {/* Text Report - Show full content */}
                        {result.data.report && (
                          <div className="border border-gray-300 rounded p-4 bg-white">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-semibold text-gray-900">Text Report</p>
                              <span className="text-xs text-gray-500">
                                {result.data.metadata?.wordCount} words
                              </span>
                            </div>
                            <div 
                              className="prose prose-sm max-w-none max-h-96 overflow-y-auto"
                              style={{
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                              }}
                            >
                              <div 
                                className="text-sm text-gray-800 leading-relaxed"
                                dangerouslySetInnerHTML={{
                                  __html: result.data.report
                                    // Convert headers
                                    .replace(/^### (.*$)/gim, '<h3 class="text-base font-bold mt-4 mb-2 text-gray-900">$1</h3>')
                                    .replace(/^## (.*$)/gim, '<h2 class="text-lg font-bold mt-5 mb-3 text-gray-900">$1</h2>')
                                    .replace(/^# (.*$)/gim, '<h1 class="text-xl font-bold mt-6 mb-4 text-gray-900">$1</h1>')
                                    // Convert bold
                                    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
                                    // Convert bullet points
                                    .replace(/^[•\-] (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
                                    // Convert numbered lists
                                    .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
                                    // Wrap consecutive li in ul
                                    .replace(/(<li.*?<\/li>\n?)+/g, '<ul class="list-disc mb-3">$&</ul>')
                                    // Convert horizontal rules
                                    .replace(/^---$/gim, '<hr class="my-4 border-gray-300" />')
                                    // Convert paragraphs (double newline)
                                    .replace(/\n\n/g, '</p><p class="mb-3">')
                                    // Wrap in paragraph
                                    .replace(/^(?!<[h|u|l|h])(.+)$/gim, '<p class="mb-3">$1</p>')
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* CSV/Excel Export */}
                        {result.data.fileName && (
                          <div className="border border-green-300 rounded p-3 bg-green-50">
                            <p className="text-sm font-semibold text-green-900 mb-2">Excel Export Ready</p>
                            <p className="text-xs text-green-700 mb-2">
                              File: {result.data.fileName} ({Math.round(result.data.fileSize / 1024)} KB)
                            </p>
                            <button 
                              className="flex items-center text-green-600 hover:text-green-700 font-medium text-sm"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`/api/ai-workflow/download/${executionResult.executionId}/${node.node_id}`, {
                                    headers: {
                                      'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                                      'x-user-email': localStorage.getItem('userEmail') || ''
                                    }
                                  });
                                  
                                  if (response.ok) {
                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = result.data.fileName;
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    document.body.removeChild(a);
                                  } else {
                                    console.error('Download failed:', response.statusText);
                                    alert('Failed to download file. Please try again.');
                                  }
                                } catch (error) {
                                  console.error('Download error:', error);
                                  alert('Failed to download file. Please try again.');
                                }
                              }}
                            >
                              <Download size={16} className="mr-2" />
                              Download Excel
                            </button>
                          </div>
                        )}
                        
                        {/* Dashboard */}
                        {result.data.dashboardConfig && (
                          <div className="border border-blue-300 rounded p-3 bg-blue-50">
                            <p className="text-sm font-semibold text-blue-900 mb-2">
                              {result.data.dashboardConfig.title}
                            </p>
                            {result.data.metrics && (
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                {Object.entries(result.data.metrics).slice(0, 4).map(([key, value]) => (
                                  <div key={key} className="bg-white p-2 rounded">
                                    <p className="text-xs text-gray-600">{key}</p>
                                    <p className="text-sm font-semibold text-gray-900">{value}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-blue-600">
                              Dashboard ID: {result.data.dashboardId}
                            </p>
                          </div>
                        )}
                        
                        {/* Chart Visualization */}
                        {result.data.chartConfig && (
                          <div className="border border-purple-300 rounded p-4 bg-purple-50">
                            <p className="text-sm font-semibold text-purple-900 mb-2">
                              {result.data.title || 'Chart Visualization'}
                            </p>
                            {result.data.description && (
                              <p className="text-xs text-purple-700 mb-3">{result.data.description}</p>
                            )}
                            <div className="bg-white p-4 rounded border border-purple-200">
                              <div className="text-xs text-gray-600 mb-2">
                                <span className="font-semibold">Type:</span> {result.data.chartType || 'bar'} chart
                              </div>
                              {result.data.chartData && result.data.chartData.labels && (
                                <div className="space-y-2">
                                  {result.data.chartData.labels.map((label, idx) => {
                                    const value = result.data.chartData.datasets?.[0]?.data?.[idx];
                                    const maxValue = Math.max(...(result.data.chartData.datasets?.[0]?.data || [1]));
                                    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
                                    
                                    return (
                                      <div key={idx} className="flex items-center">
                                        <div className="w-32 text-xs text-gray-700 truncate">{label}</div>
                                        <div className="flex-1 ml-3">
                                          <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                                            <div 
                                              className="bg-purple-500 h-full flex items-center justify-end pr-2 text-white text-xs font-semibold"
                                              style={{ width: `${percentage}%` }}
                                            >
                                              {value !== undefined && value > 0 ? value : ''}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-3 italic">
                                💡 Chart data ready for visualization
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Processing nodes - show summary */}
                    {node.node_category === 'processing' && (
                      <div className="space-y-3">
                        {result.data.executiveSummary && (
                          <div className="p-3 bg-blue-50 rounded">
                            <p className="text-sm font-semibold text-blue-900 mb-1">Executive Summary</p>
                            <p className="text-xs text-blue-800">{result.data.executiveSummary}</p>
                          </div>
                        )}
                        
                        {result.data.keyInsights && Array.isArray(result.data.keyInsights) && result.data.keyInsights.length > 0 && (
                          <div className="p-3 bg-green-50 rounded">
                            <p className="text-sm font-semibold text-green-900 mb-2">Key Insights</p>
                            <ul className="list-disc list-inside space-y-1 text-xs text-green-800">
                              {result.data.keyInsights.map((insight, i) => (
                                <li key={i}>{insight}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {result.data.fullAnalysis && (
                          <div className="p-3 bg-gray-50 rounded max-h-96 overflow-y-auto">
                            <p className="text-sm font-semibold text-gray-900 mb-2">Full Analysis</p>
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">
                              {result.data.fullAnalysis}
                            </pre>
                          </div>
                        )}
                        
                        {result.data.metadata && (
                          <div className="text-xs text-gray-600">
                            {result.data.metadata.sourceCount && (
                              <span>Sources: {result.data.metadata.sourceCount} • </span>
                            )}
                            {result.data.metadata.totalRecords !== undefined && (
                              <span>Records: {result.data.metadata.totalRecords}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Input nodes - show data summary */}
                    {node.node_category === 'input' && (
                      <div className="text-xs text-gray-700">
                        <p className="font-semibold mb-2">
                          Retrieved {result.data.metadata?.recordCount || 
                                    (Array.isArray(result.data.data) ? result.data.data.length : 0)} records
                        </p>
                        {result.data.summary && (
                          <p className="text-gray-600">{result.data.summary}</p>
                        )}
                        {result.data.metadata && (
                          <div className="mt-2 text-xs">
                            <span className="text-green-600">
                              ✓ {result.data.metadata.successCount || 0}/{result.data.metadata.queryCount || 0} queries successful
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!result.success && result.error && (
                  <div className="mt-3 p-3 bg-red-50 rounded text-sm text-red-700">
                    {result.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <GitBranch className="mr-3" size={32} />
          AI Workflow
        </h1>
        <p className="text-gray-600 mt-2">
          Describe what you need in natural language, and AI will create and execute a multi-step workflow automatically
        </p>
      </div>

      {/* Examples (if no workflow yet) */}
      {showExamples && examples.length > 0 && !workflow && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Example Workflows</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {examples.map((example, index) => (
              <button
                key={index}
                onClick={() => handleExampleClick(example.query)}
                className="text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
              >
                <p className="text-sm font-medium text-gray-900">{example.query}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Workflow Visualization */}
      {renderWorkflowGraph()}

      {/* Results */}
      {renderResults()}

      {/* Chat Input (Fixed at bottom) */}
      <div className="mt-auto pt-6 border-t border-gray-200">
        <div className="flex items-center space-x-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isGenerating && !isExecuting && handleGenerateAndExecute()}
            placeholder="Describe your workflow in natural language..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isGenerating || isExecuting}
          />
          <button
            onClick={handleGenerateAndExecute}
            disabled={isGenerating || isExecuting || !query.trim()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
          >
            {isGenerating || isExecuting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>{isGenerating ? 'Generating...' : 'Executing...'}</span>
              </>
            ) : (
              <>
                <Play size={20} />
                <span>Generate & Execute</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div ref={chatEndRef} />
    </div>
  );
};

export default AIWorkflow;

